/**
 * The pager, such as it can be.
 *
 * IT IS NOT NUMBERED AND IT NEVER WILL BE. shared.yaml's `Page` offers `items` and an opaque
 * `next_cursor` and states that offset paging is not offered — so there is no total page count, no
 * "page 4 of 9", and no Last. What can be said truthfully is "there is more" or "that is
 * everything", and this component says exactly one of those two.
 *
 * SAYING "THAT IS EVERYTHING" MATTERS. A list that just stops leaves a reader scrolling for a
 * button that is not coming.
 */
import { Button, Center, Group, Text } from '@mantine/core';
import { useTranslation } from '../../../i18n';

export interface LoadMoreProps {
  hasMore: boolean;
  isLoading: boolean;
  /** What the rows are, for the end-of-list line: "articles", "comments". */
  noun: string;
  count: number;
  onLoadMore: () => void;
}

export function LoadMore({ hasMore, isLoading, noun, count, onLoadMore }: LoadMoreProps) {
  const { t } = useTranslation('cabinet');
  if (!hasMore) {
    // `noun` is the plural ("articles"), so a list of one says it is the only one rather than
    // "That is all 1 articles" (`end_one`) - read on a real cabinet with one article (2026-10-01).
    return (
      <Center py="sm">
        <Text size="xs" c="dimmed" aria-live="polite">
          {count === 0 ? t('loadMore.none', { noun }) : t('loadMore.end', { count, noun })}
        </Text>
      </Center>
    );
  }

  return (
    <Center py="sm">
      <Group gap="sm">
        <Button variant="default" size="sm" loading={isLoading} onClick={onLoadMore}>
          {t('loadMore.button')}
        </Button>
        <Text size="xs" c="dimmed">
          {t('loadMore.soFar', { count, noun })}
        </Text>
      </Group>
    </Center>
  );
}
